import * as dcmjs from 'dcmjs';
import type {
  AppSeriesMetadata,
  GeneralViewerInstance,
} from '../../../helpers/api/view_frame/view-frames';

const { DicomMetaDictionary } = dcmjs.data;

export interface DicomMetadataRow {
  tagPunctuated: string;
  tagHex: string;
  name: string;
  vr: string;
  value: string;
}

function formatDicomValue(vr: string, value: unknown): string {
  if (value === undefined || value === null) return '';
  if (Array.isArray(value)) {
    if (value.length === 0) return '';
    if (vr === 'SQ') {
      return `[Sequence — ${value.length} item(s)]`;
    }
    return value
      .map((v) => {
        if (v !== null && typeof v === 'object' && !Array.isArray(v)) {
          if ('Alphabetic' in (v as Record<string, unknown>)) {
            const o = v as { Alphabetic?: string; Ideographic?: string };
            return o.Alphabetic || o.Ideographic || JSON.stringify(v);
          }
          return JSON.stringify(v);
        }
        return String(v);
      })
      .join('\\');
  }
  if (typeof value === 'object') {
    return JSON.stringify(value);
  }
  return String(value);
}

/**
 * Normalize DICOMweb JSON metadata to flat rows (OHIF-style table source).
 */
export function flattenDicomwebMetadata(raw: unknown): DicomMetadataRow[] {
  const objects: Record<string, { vr?: string; Value?: unknown }>[] = [];

  if (Array.isArray(raw)) {
    for (const item of raw) {
      if (item && typeof item === 'object' && !Array.isArray(item)) {
        objects.push(item as Record<string, { vr?: string; Value?: unknown }>);
      }
    }
  } else if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
    objects.push(raw as Record<string, { vr?: string; Value?: unknown }>);
  }

  const seen = new Set<string>();
  const rows: DicomMetadataRow[] = [];

  for (const obj of objects) {
    for (const [tag, meta] of Object.entries(obj)) {
      if (!/^[0-9a-fA-F]{8}$/.test(tag)) continue;
      const upper = tag.toUpperCase();
      if (seen.has(upper)) continue;
      seen.add(upper);

      const vr = (meta?.vr ?? 'UN') as string;
      const value = formatDicomValue(vr, meta?.Value);
      const punctuated =
        DicomMetaDictionary.punctuateTag(upper) ?? `(${upper.slice(0, 4)},${upper.slice(4, 8)})`;
      const dict = DicomMetaDictionary.dictionary as Record<string, { name?: string } | undefined>;
      const name = dict[punctuated]?.name ?? '';

      rows.push({
        tagPunctuated: punctuated,
        tagHex: upper,
        name,
        vr,
        value,
      });
    }
  }

  rows.sort((a, b) => parseInt(a.tagHex, 16) - parseInt(b.tagHex, 16));
  return rows;
}

/**
 * Rows sourced from DeepEcho DB + optional signed stream (native video often has no DICOMweb entry in the archive).
 */
export function buildAppInstanceMetadataRows(
  instance: GeneralViewerInstance | null,
  videoStream: { fps: number; total_frames: number } | null
): DicomMetadataRow[] {
  if (!instance) return [];
  const rows: DicomMetadataRow[] = [];
  let n = 0;
  const add = (name: string, value: string) => {
    n += 1;
    const hex = `7FDF${String(n).padStart(4, '0')}`;
    rows.push({
      tagPunctuated: '(App)',
      tagHex: hex,
      name,
      vr: 'LO',
      value,
    });
  };

  const fmt = instance.format_file ?? 'DICOM';
  add(
    'Storage',
    fmt === 'VIDEO'
      ? 'VIDEO (DeepEcho — may be MP4 in object storage, not always in DICOM archive)'
      : 'DICOM (DeepEcho)'
  );
  add('SOP Instance UID', instance.sop_instance_uid);
  add('Series Instance UID', instance.serie_instance_uid);
  add('Frame count (DeepEcho)', String(instance.frame_count));
  if (instance.modality) add('Modality (DeepEcho DB)', String(instance.modality));
  if (instance.sop_class_uid) add('SOP Class UID (DeepEcho DB)', String(instance.sop_class_uid));
  if (instance.fps != null && instance.fps !== undefined) add('FPS (DeepEcho DB)', String(instance.fps));
  if (instance.width != null && instance.height != null) {
    add('Resolution (DeepEcho DB)', `${instance.width} × ${instance.height}`);
  }
  if (videoStream) {
    add('Stream FPS (signed URL)', String(videoStream.fps));
    add('Stream frame count', String(videoStream.total_frames));
  }
  return rows;
}

/** Study/Series tab: rows from DeepEcho DB when DICOMweb series metadata is unavailable. */
export function buildAppSeriesMetadataRows(meta: AppSeriesMetadata | null): DicomMetadataRow[] {
  if (!meta) return [];
  const rows: DicomMetadataRow[] = [];
  let n = 0;
  const add = (name: string, value: string) => {
    n += 1;
    const hex = `7FDE${String(n).padStart(4, '0')}`;
    rows.push({
      tagPunctuated: '(App)',
      tagHex: hex,
      name,
      vr: 'LO',
      value,
    });
  };

  add('Source', 'DeepEcho database (not DICOM archive)');
  add('Study Instance UID', meta.study_instance_uid);
  add('Series Instance UID', meta.serie_instance_uid);
  if (meta.serie_type) add('Series type (DeepEcho)', String(meta.serie_type));
  add('Instance count', String(meta.instance_count));
  add('VIDEO instances', String(meta.video_instance_count));
  add('DICOM instances', String(meta.dicom_instance_count));
  add('Total frames (all instances)', String(meta.total_frames_all_instances));
  if (meta.modalities?.length) add('Modalities (instances)', meta.modalities.join(', '));
  if (meta.global_pixel_spacing != null) {
    add('Global pixel spacing (serie)', JSON.stringify(meta.global_pixel_spacing));
  }
  if (meta.first_video_fps != null) add('First VIDEO FPS (DB)', String(meta.first_video_fps));
  if (meta.first_video_width != null && meta.first_video_height != null) {
    add('First VIDEO resolution (DB)', `${meta.first_video_width} × ${meta.first_video_height}`);
  }
  if (meta.first_dicom_modality) add('First DICOM modality (DB)', meta.first_dicom_modality);
  return rows;
}
